import { create } from 'zustand'

import type { SaveResult } from '@/hooks/useEditorDraft'

/**
 * The one thing the close coordinator needs from a tab: a way to flush its
 * unsaved work. Editors register through `useWorkspaceCloseOwner`.
 */
export type CloseOwner = {
  save: () => Promise<SaveResult>
}

/**
 * How a close request ended. The tab is either gone or still open — an id that
 * was never open reports 'closed' too, because it is not open either.
 */
export type CloseOutcome = 'closed' | 'cancelled'

interface CloseOwnersState {
  owners: Map<string, CloseOwner>
  register: (tabId: string, owner: CloseOwner) => void
  /**
   * Removes the registration only when `owner` is still the registered object.
   * A remount registers the new owner before the old one's cleanup runs, so an
   * unconditional delete would drop the live owner.
   */
  unregister: (tabId: string, owner: CloseOwner) => void
}

// Same immutability contract as tabDrafts: every mutation replaces the Map so
// selector subscribers see a referential change.
export const useCloseOwners = create<CloseOwnersState>()((set) => ({
  owners: new Map(),

  register: (tabId, owner) =>
    set((s) => ({ owners: new Map(s.owners).set(tabId, owner) })),

  unregister: (tabId, owner) =>
    set((s) => {
      if (s.owners.get(tabId) !== owner) return s
      const next = new Map(s.owners)
      next.delete(tabId)
      return { owners: next }
    }),
}))

/**
 * The close request currently waiting on the user. At most one at a time.
 * Only the id is parked — the title and the close action are read live, so a
 * rename or a re-render while the dialog is up cannot serve a stale value.
 */
type PendingClose = {
  tabId: string
  resolve: (outcome: CloseOutcome) => void
}

interface PendingCloseState {
  pending: PendingClose | null
  open: (pending: PendingClose) => void
  /** Settles the pending request with `outcome` and clears it. No-op if none. */
  settle: (outcome: CloseOutcome) => void
}

export const usePendingClose = create<PendingCloseState>()((set, get) => ({
  pending: null,

  open: (pending) => set({ pending }),

  settle: (outcome) => {
    const { pending } = get()
    if (!pending) return
    set({ pending: null })
    pending.resolve(outcome)
  },
}))
